"use client";

import type { AppErrorCode } from "@/lib/error-config";
import { RegistrationError } from "@/lib/app-error";
import { callOperation as op } from "@/lib/f360-client";

export { RegistrationError } from "@/lib/app-error";

/* Facility registration against onboarding_v3 (healthpro_erp).
   Flow (all guest, no login required):
     1. verifyRegistration -> facility registration number; the registry names
        the administrator, whose CR contact gets the code. Returns a
        registrationSession that stands in for the ID until the code is verified.
        (verifyIdentity is the older ID-first entry, kept for compatibility.)
     2. getFacilityDetails -> ownership re-check + canonical HFR record
     3. onboardFacility -> username/email/password + gap fields -> verification email
     4. resendVerification + register_verify_email (existing verifier)
   Every backend call goes through the F360 operation route
   (lib/f360-client.ts → POST /api/f360/<operation>); the Frappe method
   names live server-side in lib/server/operations.ts. The route already
   unwraps the onboarding_v3 envelope, so `op()` resolves straight to `data`
   and rejects with a RegistrationError carrying the backend's reason as
   `detail`; `code` stays a typed fallback for copy. */

// ── Types used by the screens ─────────────────────────────────────────────

export type IdType = "National ID" | "Passport" | "Alien ID";

export type V3FacilitySummary = {
  facilityFid: string;
  name: string;
  code: string;
  registrationNumber: string;
  type: string;
  county: string;
  subCounty: string;
  operationalStatus: string;
  isOnboarded: boolean;
};

export type V3AdminProfile = {
  firstName: string;
  middleName: string;
  lastName: string;
  phoneNumber: string;
  gender: string;
  dateOfBirth: string;
  clientRegistryId: string;
};

export type V3Identity = {
  alreadyRegistered: boolean;
  otpVerified: boolean;
  identityToken: string;
  identificationType: string;
  identificationNumber: string;
  isRegisteredOnCR: boolean;
  maskedEmail: string;
  adminEmailRequired: boolean;
  adminProfile: V3AdminProfile;
  missingAdminFields: string[];
  facilities: V3FacilitySummary[];
  availableFacilities: V3FacilitySummary[];
  totalFacilities: number;
  availableCount: number;
};

/** Step-1 response: the OTP was sent, facilities stay withheld until Step 1b. */
export type V3OtpSession = {
  alreadyRegistered: boolean;
  otpRecord: string;
  otpChannel: string;
  maskedDestination: string;
  otpExpiryMinutes: number;
  identificationType: string;
  identificationNumber: string;
  maskedEmail: string;
};

export type OtpChannel = "auto" | "sms" | "email";

/** Step-1 response when starting from the facility registration number. */
export type V3RegistrationOtpSession = V3OtpSession & {
  registrationSession: string;
  registrationNumber: string;
  facilityName: string;
  maskedIdentificationNumber: string;
};

export type V3FacilityDetails = {
  facility: Record<string, string>;
  isRegisteredOnCR: boolean;
  adminEmailRequired: boolean;
  adminPrefill: V3AdminProfile & { maskedEmail: string; email?: string };
  missingAdminFields: string[];
};

export type OnboardAccountInput = {
  username: string;
  email: string;
  password: string;
  confirmPassword: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  phoneNumber?: string;
  gender?: string;
  dateOfBirth?: string;
};

export type V3OnboardResult = {
  facilityName: string;
  registrationNumber: string;
  hieId: string;
  username: string;
  maskedEmail: string;
  verificationExpiryHours: number;
  nextStep: string;
  linked?: boolean;
};

/** Legacy aliases kept so existing UI cards keep compiling during migration. */
export type RegisteredAdmin = { email: string; maskedEmail: string; isNewUser: boolean };
export type RegistryFacility = {
  facilityId: string;
  name: string;
  code: string;
  registrationNumber: string;
  type: string;
  level: string;
  county: string;
  subCounty: string;
  ownerType: string;
  owner: string;
  alreadyOnboarded: boolean;
};
export type OwnerCodeSession = { otpId: string; channel: string; maskedDestination: string; expiresInSeconds: number; resendCooldownSeconds: number };
export type FacilityAdditionalDetails = {
  ownerType: string;
  owner: string;
  ownerKraPin: string;
  ownerBoardRegistrationNumber: string;
  physicalAddress: string;
  email: string;
  county: string;
  subCounty: string;
  ward: string;
  constituency: string;
  region: string;
  latitude: string;
  longitude: string;
  numberOfBeds: string;
  maximumBedAllocation: string;
  openWholeDay: boolean;
  openWeekends: boolean;
  openPublicHoliday: boolean;
  openLateNight: boolean;
};
export type VerifiedFacility = {
  facilityId: string;
  name: string;
  code: string;
  registrationNumber: string;
  type: string;
  category: string;
  level: string;
  operationalStatus: string;
  admin: { fullName: string; email: string; phone: string };
  license: { number: string; type: string; expiryDate: string; regulatoryBody: string };
  defaults: FacilityAdditionalDetails;
};
export type ReferenceData = { regions: { id: string; label: string }[]; publicOwnerTypes: string[] };
export type FacilityContact = { name: string; phone: string };
export type FacilityBank = { bankName: string; branchName: string; accountName: string; accountNumber: string };
export type OnboardedFacility = { reference: string; facilityName: string };

// ── Error handling ─────────────────────────────────────────────────────────

type Envelope = { status?: string; message?: unknown; data?: unknown };

/** Pull a human-readable message out of an unexpected throw, if any.
    Operation-route failures already carry `detail`; this covers anything
    else (e.g. a TypeError) so screens still get safe copy. */
function serverDetail(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;
  const bag = error as Record<string, unknown>;
  for (const key of ["message", "error", "_server_messages", "response", "data"]) {
    const value = bag[key];
    if (typeof value === "string" && value.trim() && value !== "Forbidden" && !/^\d+$/.test(value.trim())) {
      // Frappe sometimes nests JSON-encoded server messages.
      if (key === "_server_messages") {
        try {
          const parsed = JSON.parse(value) as unknown;
          const first = Array.isArray(parsed) ? parsed[0] : parsed;
          const msg = typeof first === "string" ? JSON.parse(first) as { message?: string } : first as { message?: string };
          if (msg?.message) return stripHtml(msg.message);
        } catch { return undefined; }
      }
      // Skip generic Error("UNKNOWN") etc.
      if (value.length < 200) return value;
    }
  }
  // frappe-js-sdk nests the body under response.data.message
  try {
    const nested = (bag.response as { data?: unknown })?.data as Envelope | undefined;
    const inner = nested?.message && typeof nested.message === "object" ? (nested.message as Envelope) : nested;
    if (inner && typeof inner.message === "string") return inner.message;
  } catch { /* ignore */ }
  return undefined;
}

function stripHtml(value: string) {
  return value.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function statusOf(error: unknown): number {
  if (error instanceof RegistrationError) return error.status;
  if (error && typeof error === "object" && ("httpStatus" in error || "status" in error)) return Number(((error as { httpStatus?: unknown }).httpStatus ?? (error as { status?: unknown }).status)) || 0;
  return 0;
}

function codeFor(status: number, detail?: string): AppErrorCode {
  const haystack = (detail ?? "").toLowerCase();
  // Backend sometimes answers HTTP 200 with {status:"error"} - route by
  // message first so the right copy shows even when the status is generic.
  if (haystack.includes("no facilities found")) return "REGISTRY_NOT_FOUND";
  // "regulator to update" alone is not enough: registry data gaps say it too.
  if (haystack.includes("not assigned") || haystack.includes("different owner") || haystack.includes("does not list you")) return "OWNER_MISMATCH";
  // The administrator's client registry (Afyangu) record, before the facility record's "registry record" below.
  if (haystack.includes("client registry could not be reached")) return "CLIENT_REGISTRY_UNAVAILABLE";
  if (haystack.includes("client registry record") && haystack.includes("no email")) return "CLIENT_REGISTRY_NO_EMAIL";
  if (haystack.includes("no client registry record") || haystack.includes("register on afyangu")) return "CLIENT_REGISTRY_MISSING";
  if (haystack.includes("no administrator recorded") || haystack.includes("registry record")) return "REGISTRY_RECORD_INCOMPLETE";
  if (haystack.includes("already been onboarded")) return "FACILITY_ALREADY_ONBOARDED";
  if (haystack.includes("username") && haystack.includes("taken")) return "USERNAME_TAKEN";
  if (haystack.includes("already registered") || haystack.includes("already exists") || haystack.includes("already verified")) return "ACCOUNT_EXISTS";
  if (haystack.includes("profile is incomplete") || haystack.includes("please provide")) return "PROFILE_INCOMPLETE";
  // Frappe's zxcvbn rejection ("similar to a commonly used password",
  // "easy to guess", …) - not a wrong code or a missing field.
  if (/common password|commonly used|easy to guess|invalid password|capitalization doesn't help/.test(haystack)) return "PASSWORD_TOO_WEAK";
  if (haystack.includes("password")) return "PASSWORD_NOT_SET";
  if (status === 0) return "NETWORK_ERROR";
  if (status === 401) return "SESSION_EXPIRED";
  if (status === 429) return "RATE_LIMITED";
  if (status === 404) return "REGISTRY_NOT_FOUND";
  if (status === 403) return "OWNER_MISMATCH";
  if (status === 409) {
    if (haystack.includes("username")) return "USERNAME_TAKEN";
    if (haystack.includes("email")) return "ACCOUNT_EXISTS";
    if (haystack.includes("identification") || haystack.includes("already registered")) return "ACCOUNT_EXISTS";
    return "FACILITY_ALREADY_ONBOARDED";
  }
  if (status === 422) return "PROFILE_INCOMPLETE";
  if (status >= 500) return "INTERNAL_SERVER_ERROR";
  if (status === 400) {
    if (haystack.includes("password")) return "PASSWORD_NOT_SET";
    return "ONBOARDING_REJECTED";
  }
  return "UNKNOWN";
}

function toRegistrationError(error: unknown, fallbackStatus?: number): RegistrationError {
  if (error instanceof RegistrationError) {
    if (error.code === "UNKNOWN") {
      const detail = error.detail ?? serverDetail(error);
      return new RegistrationError(codeFor(error.status, detail), error.status, detail);
    }
    return error;
  }
  const detail = serverDetail(error);
  const status = statusOf(error) || fallbackStatus || 0;
  return new RegistrationError(codeFor(status, detail), status, detail);
}

const text = (value: unknown) => (value === null || value === undefined ? "" : String(value).trim());
const flag = (value: unknown) => value === true || value === 1 || value === "1";

/** Constant-time string comparison for token-shaped values (timing-attack safe). */
export function constantTimeEqual(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let index = 0; index < a.length; index++) diff |= a.charCodeAt(index) ^ b.charCodeAt(index);
  return diff === 0;
}

// ── Mappers ────────────────────────────────────────────────────────────────

type Raw = Record<string, unknown>;

function mapOtpSession(data: Raw, idType: string, idNumber: string, channel: OtpChannel = "auto"): V3OtpSession {
  return {
    alreadyRegistered: flag(data.already_registered),
    otpRecord: text(data.otp_record),
    otpChannel: text(data.otp_channel) || channel,
    maskedDestination: text(data.masked_destination),
    otpExpiryMinutes: Number(data.otp_expiry_minutes) || 5,
    identificationType: text(data.identification_type) || idType,
    identificationNumber: text(data.identification_number) || idNumber.trim(),
    maskedEmail: text(data.masked_email),
  };
}

function mapFacilitySummary(row: Raw): V3FacilitySummary {
  return {
    facilityFid: text(row.facility_fid ?? row.facility_id ?? row.hie_id),
    name: text(row.facility_name),
    code: text(row.facility_code),
    registrationNumber: text(row.registration_number),
    type: text(row.facility_type),
    county: text(row.county),
    subCounty: text(row.sub_county),
    operationalStatus: text(row.operational_status),
    isOnboarded: flag(row.is_onboarded),
  };
}

function mapProfile(raw: Raw): V3AdminProfile {
  return {
    firstName: text(raw.first_name),
    middleName: text(raw.middle_name),
    lastName: text(raw.last_name),
    phoneNumber: text(raw.phone_number),
    gender: text(raw.gender),
    dateOfBirth: text(raw.date_of_birth),
    clientRegistryId: text(raw.client_registry_id),
  };
}

/** Reuse the existing facility cards with zero component changes. */
export function toLegacyRegistryFacility(summary: V3FacilitySummary): RegistryFacility {
  return {
    facilityId: summary.facilityFid,
    name: summary.name,
    code: summary.code,
    registrationNumber: summary.registrationNumber,
    type: summary.type,
    level: "",
    county: summary.county,
    subCounty: summary.subCounty,
    ownerType: "",
    owner: "",
    alreadyOnboarded: summary.isOnboarded,
  };
}

export function maskEmail(email: string) {
  const [name, domain] = email.split("@");
  if (!domain) return "your registered email";
  return `${name.slice(0, 1)}${"•".repeat(Math.max(2, Math.min(6, name.length - 1)))}@${domain}`;
}

/** Step-1b payload: withheld facility list + profile + identity token. */
function mapIdentity(data: Raw, idType: string, idNumber: string): V3Identity {
  const facilities = Array.isArray(data.facilities) ? (data.facilities as Raw[]).map(mapFacilitySummary) : [];
  const availableRaw = Array.isArray(data.available_facilities) ? (data.available_facilities as Raw[]) : undefined;
  const available = availableRaw ? availableRaw.map(mapFacilitySummary) : facilities.filter((f) => !f.isOnboarded);
  return {
    alreadyRegistered: flag(data.already_registered),
    otpVerified: flag(data.otp_verified) || true,
    identityToken: text(data.identity_token),
    identificationType: text(data.identification_type) || idType,
    identificationNumber: text(data.identification_number) || idNumber.trim(),
    isRegisteredOnCR: flag(data.is_registered_on_cr),
    maskedEmail: text(data.masked_email),
    adminEmailRequired: flag(data.admin_email_required),
    adminProfile: mapProfile((data.admin_profile as Raw) ?? {}),
    missingAdminFields: Array.isArray(data.missing_admin_fields) ? (data.missing_admin_fields as unknown[]).map(text) : [],
    facilities,
    availableFacilities: available,
    totalFacilities: Number(data.total_facilities) || facilities.length,
    availableCount: Number(data.available_count) || available.length,
  };
}

// ── Service ────────────────────────────────────────────────────────────────

export function createRegistrationService() {
  return {
    async verifyIdentity(idType: IdType, idNumber: string, channel: OtpChannel = "auto"): Promise<V3OtpSession> {
      try {
        const data = await op<Raw>("onboarding.verify-identity", { identification_type: idType, identification_number: idNumber.trim(), ...(channel === "auto" ? {} : { channel }) });
        return mapOtpSession(data, idType, idNumber);
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

    /* Step 1 by facility: the administrator's ID stays server-side; only
       masked hints and the registrationSession come back. */
    async verifyRegistration(registrationNumber: string, channel: OtpChannel = "auto"): Promise<V3RegistrationOtpSession> {
      try {
        const data = await op<Raw>("onboarding.verify-registration", { registration_number: registrationNumber.trim(), ...(channel === "auto" ? {} : { channel }) });
        return {
          ...mapOtpSession(data, "National ID", ""),
          registrationSession: text(data.registration_session),
          registrationNumber: text(data.registration_number) || registrationNumber.trim(),
          facilityName: text(data.facility_name),
          maskedIdentificationNumber: text(data.masked_identification_number),
        };
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

    /* Step 1b: code check -> facility list + profile + identityToken.
       The token must accompany Steps 2-3. */
    async verifyOtp(idType: string, idNumber: string, otpRecord: string, otp: string, registrationSession?: string): Promise<V3Identity> {
      try {
        const who = registrationSession ? { registration_session: registrationSession } : { identification_type: idType, identification_number: idNumber.trim() };
        const data = await op<Raw>("onboarding.verify-otp", { ...who, otp_record: otpRecord, otp: otp.trim() });
        return mapIdentity(data, idType, idNumber);
      } catch (error) {
        const failure = toRegistrationError(error);
        // The backend answers a wrong or expired code with 401: that is a
        // rejected code on a guest step, not an ended session.
        throw failure.code === "SESSION_EXPIRED" ? new RegistrationError("VERIFICATION_FAILED", failure.status, failure.detail) : failure;
      }
    },

    /* Step 1c: new code, optionally on the other channel. Always submit
       the latest otp_record afterwards. */
    async resendOtp(idType: string, idNumber: string, channel: OtpChannel = "auto", registrationSession?: string): Promise<V3OtpSession> {
      try {
        const who = registrationSession ? { registration_session: registrationSession } : { identification_type: idType, identification_number: idNumber.trim() };
        const data = await op<Raw>("onboarding.resend-otp", { ...who, ...(channel === "auto" ? {} : { channel }) });
        return mapOtpSession(data, idType, idNumber, channel);
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

    async getFacilityDetails(idType: string, idNumber: string, facilityId: string, identityToken: string): Promise<V3FacilityDetails> {
      try {
        const data = await op<Raw>("onboarding.facility-details", { identification_type: idType, identification_number: idNumber, facility_id: facilityId, identity_token: identityToken });
        const facilityRaw = (data.facility_details as Raw) ?? {};
        const facility: Record<string, string> = {};
        for (const [key, value] of Object.entries(facilityRaw)) facility[key] = text(value);
        const prefillRaw = (data.admin_prefill as Raw) ?? {};
        const prefillEmail = text(prefillRaw.email).toLowerCase();
        return {
          facility,
          isRegisteredOnCR: flag(data.is_registered_on_cr),
          adminEmailRequired: flag(data.admin_email_required),
          adminPrefill: {
            ...mapProfile(prefillRaw),
            maskedEmail: text(prefillRaw.masked_email),
            ...(prefillEmail ? { email: prefillEmail } : {}),
          },
          missingAdminFields: Array.isArray(data.missing_admin_fields) ? (data.missing_admin_fields as unknown[]).map(text) : [],
        };
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

async onboardFacility(args: {
      username: string;
      email: string;
      password: string;
      confirmPassword: string;
      identificationType: string;
      identificationNumber: string;
      facilityId: string;
      identityToken: string;
      profile?: Partial<Record<"first_name" | "middle_name" | "last_name" | "phone_number" | "gender" | "date_of_birth", string>>;
    }): Promise<V3OnboardResult> {
      const { identityToken, ...rest } = args;
      try {
        const data = await op<Raw>("onboarding.submit", {
          username: args.username.trim(),
          email: args.email.trim(),
          password: args.password,
          confirm_password: args.confirmPassword,
          identification_type: args.identificationType,
          identification_number: args.identificationNumber,
          facility_id: args.facilityId,
          identity_token: identityToken,
          ...(args.profile ?? {}),
        });
        const facility = (data.facility as Raw) ?? {};
        const admin = (data.admin as Raw) ?? {};
        return {
          facilityName: text(facility.facility_name),
          registrationNumber: text(facility.registration_number),
          hieId: text(facility.hie_id),
          username: text(admin.username) || args.username.trim(),
          maskedEmail: text(admin.masked_email) || maskEmail(args.email),
          verificationExpiryHours: Number(data.verification_expiry_hours) || 24,
          nextStep: text(data.next_step),
          linked: (data.linked as boolean) === true,
        };
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

    async resendVerification(email: string): Promise<{ maskedEmail: string; expiryHours: number }> {
      try {
        const data = await op<Raw>("onboarding.resend-verification", { email: email.trim() });
        return { maskedEmail: text(data.masked_email) || maskEmail(email), expiryHours: Number(data.verification_expiry_hours) || 24 };
      } catch (error) {
        throw toRegistrationError(error);
      }
    },

    /** Step 4 of onboarding_v3: verify the emailed link (guest, single-use vid). */
    async verifyEmail(vid: string): Promise<{ message: string; alreadyVerified: boolean }> {
      try {
        // The verifier answers {success, message, already_verified?}; the route returns it as-is (shape: "raw").
        const inner = (await op<{ success?: unknown; message?: unknown; already_verified?: unknown } | null>("onboarding.verify-email", { vid })) ?? {};
        if (inner.success === true) return { message: text(inner.message) || "Email verified and account activated successfully.", alreadyVerified: inner.already_verified === true };
        throw new RegistrationError("VERIFICATION_FAILED", 400, text(inner.message) || undefined);
      } catch (error) {
        throw toRegistrationError(error, 400);
      }
    },
  };
}

export type RegistrationService = ReturnType<typeof createRegistrationService>;

// Stateless: one instance, so effects keyed on it never re-run needlessly.
const registrationService = createRegistrationService();

export function useRegistrationService(): RegistrationService {
  return registrationService;
}

/* Short-lived hand-off between the public steps. Holds no password. */
const SIGNUP_IDENTITY_KEY = "facility360_signup_identity_v3";
const SIGNUP_OTP_KEY = "facility360_signup_otp_v3";
const SIGNUP_RESULT_KEY = "facility360_signup_result";

/** idNumber is empty when step 1 started from the registration number:
    registrationSession then identifies the administrator until the code is verified. */
export type SignupOtp = { idType: string; idNumber: string; session: V3OtpSession; registrationSession?: string; registrationNumber?: string; facilityName?: string };
export function saveSignupOtp(value: SignupOtp) { window.sessionStorage.setItem(SIGNUP_OTP_KEY, JSON.stringify(value)); }
export function readSignupOtp(): SignupOtp | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(SIGNUP_OTP_KEY) ?? "null") as SignupOtp | null;
    return value && typeof value.idNumber === "string" && value.session?.otpRecord ? value : null;
  } catch { return null; }
}
export function clearSignupOtp() { window.sessionStorage.removeItem(SIGNUP_OTP_KEY); }

export type SignupIdentity = { idType: string; idNumber: string; identity: V3Identity; selectedFacilityId?: string; /** Step 2 (phone OTP) passed. */ phoneVerified?: boolean };
export function saveSignupIdentity(value: SignupIdentity) { window.sessionStorage.setItem(SIGNUP_IDENTITY_KEY, JSON.stringify(value)); }
export function readSignupIdentity(): SignupIdentity | null {
  try {
    const value = JSON.parse(window.sessionStorage.getItem(SIGNUP_IDENTITY_KEY) ?? "null") as SignupIdentity | null;
    return value && typeof value.idNumber === "string" && value.identity ? value : null;
  } catch { return null; }
}
export function clearSignupIdentity() { window.sessionStorage.removeItem(SIGNUP_IDENTITY_KEY); }

export type SignupResult = V3OnboardResult & { adminEmail: string };
export function saveSignupResult(result: SignupResult) { window.sessionStorage.setItem(SIGNUP_RESULT_KEY, JSON.stringify(result)); }
export function readSignupResult(): SignupResult | null {
  try { return JSON.parse(window.sessionStorage.getItem(SIGNUP_RESULT_KEY) ?? "null") as SignupResult | null; } catch { return null; }
}

/** @deprecated v3 has no separate password-OTP step. Kept for old imports. */
export function saveSignupAccount(account: RegisteredAdmin) { window.sessionStorage.setItem("facility360_signup_account", JSON.stringify(account)); }
/** @deprecated */
export function readSignupAccount(): RegisteredAdmin | null {
  try { const value = JSON.parse(window.sessionStorage.getItem("facility360_signup_account") ?? "null"); return value && typeof value.email === "string" ? value : null; } catch { return null; }
}
/** @deprecated */
export function clearSignupAccount() { window.sessionStorage.removeItem("facility360_signup_account"); }